<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api } from '@/api/client'
import { useRunSocket } from '@/composables/useRunSocket'
import RunStatusBadge from '@/components/RunStatusBadge.vue'
import type { ResourceRef } from '@/lib/resource-links'
import { cachedPreviewFetch } from '@/lib/preview-cache'

const props = defineProps<{ resourceRef: ResourceRef }>()

const loading = ref(true)
const run = ref<{ status: string } | null>(null)
const tailLines = ref<string[]>([])
const { subscribe, unsubscribeAll } = useRunSocket()
let cancelled = false

const runId = computed(() => Number(props.resourceRef.id))

async function loadLogTail(snapshot = true) {
  try {
    const url = snapshot
      ? `/runs/${runId.value}/logs?snapshot=true&tail=4096`
      : `/runs/${runId.value}/logs?tail=4096`
    const text = await api.get<string>(url)
    if (typeof text === 'string') {
      const raw = text.split('\n').filter(l => l.length > 0)
      const lines = raw.some(l => l.startsWith('event: '))
        ? raw.filter(l => l.startsWith('data: ')).map(l => l.slice(6))
        : raw
      tailLines.value = lines.slice(-6)
    }
  } catch {
    tailLines.value = []
  }
}

onMounted(async () => {
  try {
    run.value = await cachedPreviewFetch(`run:${runId.value}`, () =>
      api.get(`/runs/${runId.value}`),
    )
    const status = run.value?.status ?? ''
    if (['success', 'failed', 'completed', 'cancelled'].includes(status)) {
      await loadLogTail(false)
    } else {
      await loadLogTail(true)
      subscribe(runId.value, {
        onLog(lines) {
          tailLines.value = [...tailLines.value, ...lines].slice(-6)
        },
      })
    }
  } catch {
    run.value = null
  } finally {
    if (!cancelled) loading.value = false
  }
})

onUnmounted(() => {
  cancelled = true
  unsubscribeAll()
})
</script>

<template>
  <div class="p-2 text-xs space-y-1.5 max-w-[280px] min-w-0">
    <p class="font-medium">Run {{ resourceRef.id }}</p>
    <p v-if="loading" class="text-muted-foreground">Loading…</p>
    <template v-else>
      <RunStatusBadge v-if="run" :status="run.status" />
      <div v-if="tailLines.length" class="font-mono text-[10px] text-muted-foreground max-h-24 overflow-hidden border-t border-border/50 pt-1.5 mt-1">
        <div v-for="(line, i) in tailLines" :key="i" class="truncate">{{ line }}</div>
      </div>
      <p class="text-[10px] text-muted-foreground">Open run for full logs →</p>
    </template>
  </div>
</template>
